import '@css/Map.css'
import 'mapbox-gl/dist/mapbox-gl.css'

import MainWrapper from '@components/Utils/MainWrapper'
import useApi from '@hooks/useApi'
import { GlobalStyles } from '@mui/material'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'
import type { PrayerTime } from '@shared_types/prayer_times'
import { is24HTimeSystem } from '@utils/time_utils'
import { isString } from 'lodash'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'

import type { MuiCustomStyle } from '../../types/mui_style_types'
import YisTable from '../material/YisTable'

const color = '#33C1FA'

// All of these classes are passed to <YisTable /> as plain string props
// (tableHeader/headerDiv/bodyCellStyle/headerCellStyle/bodyRowStyle/activeBody/
// className), which apply them to YisTable's own internal table DOM
// (TableHead/TableRow/TableCell/HeaderContent) that we do not render here.
// They therefore cannot be turned into styled JSX elements and are
// deliberately kept as a scoped GlobalStyles bridge.
const prayerTableClasses = {
  bodyRow: 'prayer-times-body-row',
  header: 'prayer-times-header',
  body: 'prayer-times-body',
  headerDiv: 'prayer-times-header-div',
  tableHeader: 'prayer-times-table-header',
  table: 'prayer-times-table',
  bodyActive: 'prayer-times-body-active'
}

const PrayerContainer = styled(Grid)({
  position: 'fixed' as MuiCustomStyle,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: '#06375290!important',
  backdropFilter: 'blur(6px)',
  zIndex: 10000000000000
})

const PrayerMainWrapper = styled(MainWrapper)({
  display: 'block',
  marginTop: '3rem'
})

const prayerTableStyles = {
  [`.${prayerTableClasses.bodyRow}`]: {
    border: '1px solid transparent!important',
    borderRadius: 15,
    marginTop: '.5rem',
    cursor: 'initial!important',
    '&:hover': {
      backgroundColor: 'transparent!important'
    },
    '&:nth-child(5)': {
      border: `1px solid ${color}!important`,
      backgroundColor: 'transparent!important'
    }
  },
  [`.${prayerTableClasses.header}`]: {
    '&:first-child': {
      paddingLeft: '.5rem !important'
    },
    borderBottom: `2px solid ${color} !important`,
    justifyContent: 'center',
    paddingBottom: '.5rem !important'
  },
  [`.${prayerTableClasses.body}`]: {
    border: 'none !important',
    borderRadius: '15px !important',
    textAlign: 'center !important' as MuiCustomStyle,
    fontWeight: '500 !important',
    margin: 'auto !important',
    transition: 'background-color .2s ease !important',
    position: 'relative' as MuiCustomStyle,
    cursor: 'arrow!important',
    padding: '0.3rem !important'
  },
  [`.${prayerTableClasses.headerDiv}`]: {
    justifyContent: 'center!important',
    fontWeight: 600
  },
  [`.${prayerTableClasses.tableHeader}`]: {
    marginBottom: '1rem'
  },
  [`.${prayerTableClasses.table}`]: {
    marginTop: '4.5rem',
    marginBottom: '10rem'
  },
  [`.${prayerTableClasses.bodyActive}`]: {
    backgroundColor: color
  }
}

type Props = { close: () => void }
type Event = { date: string; index: number }

const convertToPM = (time: string) => {
  if (isString(time)) {
    const timeString = time
    const H = +timeString.substring(0, 2)
    const h = H % 12 || 12
    const ampm = H < 12 || H === 24 ? ' AM' : ' PM'
    return h + timeString.substring(2, 5) + ampm
  }

  return ''
}

export default function PrayerTimes ({ close }: Props) {
  const headers = [
    { name: moment().format('MMMM'), value: 'date' },
    { name: 'Fajr', value: 'Fajr' },
    { name: 'Sunrise', value: 'Sunrise' },
    { name: 'Dhuhr', value: 'Dhuhr' },
    { name: 'ASR', value: 'Asr' },
    { name: 'Maghrib', value: 'Maghrib' },
    { name: 'Isha', value: 'Isha' }
  ]

  const { apiGetArray } = useApi()
  const [prayerTimes, setPrayerTimes] = useState<PrayerTime[]>([])
  const [events, setEvents] = useState<Event[]>([])
  const [closestEvent, setClosestEvent] = useState<Event | null>(null)

  const fetchData = () => {
    const now = moment()

    apiGetArray<PrayerTime & { id: number, date: string }>('/api/prayer-times').then(connectedData => {
      const today = now.format('DD ddd').toUpperCase()

      const finalData = connectedData.map(data => {
        let newObj = {}

        for (const key in data) {
          // @ts-expect-error string can't be used to index type PrayerTime
          const time = isString(data[key]) ? data[key].split(' ')[0] : ''

          if (data.date === today && key !== 'date') {
            setEvents(e => [...e, { date: time } as Event])
          }

          const timeString = is24HTimeSystem() ? time : convertToPM(time)
          const newValue = key === 'date' ? data[key] : timeString

          newObj = { ...newObj, [key]: newValue }
        }
        return newObj as PrayerTime
      })

      setPrayerTimes(finalData)
    })
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (events && events.length) {
      for (let i = 0; i <= events.length; i++) {
        if (events[i] && events[i].date > moment(Date.now()).format('HH:mm')) {
          setClosestEvent({ ...events[i], index: i + 1 })
          break
        }
      }
    }
  }, [events])

  return <PrayerContainer container>
    <GlobalStyles styles={prayerTableStyles} />
    <PrayerMainWrapper showBackBtn title="Makkah prayer times" onClose={close}>
      <YisTable
        isMecca
        headers={headers}
        body={prayerTimes}
        emptyMessage="Loading..."
        tableHeader={prayerTableClasses.tableHeader}
        headerDiv={prayerTableClasses.headerDiv}
        bodyCellStyle={prayerTableClasses.body}
        headerCellStyle={prayerTableClasses.header}
        bodyRowStyle={prayerTableClasses.bodyRow}
        className={`${prayerTableClasses.table} pray-table`}
        activeBody={{
          colIndex: closestEvent ? closestEvent.index : null,
          rowIndex: 4,
          class: prayerTableClasses.bodyActive
        }}
        sortable={false}
      />
    </PrayerMainWrapper>
  </PrayerContainer>
}
